import React, { useEffect, useRef, useState } from "react";
import { bool, func, shape, string } from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";

import { resizableRoute } from "pages/_components/Resizable";
import { goBack } from "react-router-redux";
import Yup from "yup";

import { CreateContact, Text } from "@ui-kit/components/index.es";

import Head from "pages/_components/Head";
import Notification from "pages/_components/Notification";

import { actions as agendaActions, selectors as agendaSelectors } from "reducers/frequentDestination";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as statusSelectors } from "reducers/status";
import { actions as layoutActions } from "reducers/layout";

import * as i18n from "util/i18n";
import * as config from "util/config";
import { isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";

import * as transferMiddleware from "middleware/transfer";
import * as frequentDestinationMidleware from "middleware/frequentDestination";

const FORM_ID = "agenda.create";
const MAX_REQUIRED = "22";
const CREATE_CONTACT_PATH = "/v2/agenda/create";
const AGENDA_DEFAULT_BACK_PATH = "/v2/agenda";

function resolveCreateContactGoBackPath(lastHrefPathname) {
    if (
        typeof lastHrefPathname === "string" &&
        lastHrefPathname.startsWith("/") &&
        lastHrefPathname !== CREATE_CONTACT_PATH
    ) {
        return lastHrefPathname;
    }
    return AGENDA_DEFAULT_BACK_PATH;
}
function CreateContactPage({ isDesktop, dispatch, contact, fetching, embeddedInV3Shell, lastHrefPathname }) {
    const [formikProps, setFormikProps] = useState({});
    const [rememberValues, setRememberValues] = useState(false);
    const didPrefillSearch = useRef(false);
    const uxImprovementsDasboard = isUxDashboardImprovementsEnabled();
    const prefillAccount = contact?.cbu || "";

    useEffect(() => {
        dispatch(layoutActions.setTitleKey("agenda.create.title"));
        dispatch(layoutActions.setGoBackPath(resolveCreateContactGoBackPath(lastHrefPathname)));
        dispatch(layoutActions.setRightContentMobile(null));
    }, [dispatch, lastHrefPathname]);

    useEffect(() => {
        if (!embeddedInV3Shell || !isUxDashboardImprovementsEnabled()) {
            return undefined;
        }
        dispatch(layoutActions.setScopes(["FrequentDestinationsCreate"]));
        return () => {
            dispatch(layoutActions.setScopes([]));
        };
    }, [dispatch, embeddedInV3Shell]);

    const handleBack = () => dispatch(goBack());

    const isValidCbu = (value) => {
        const reg = /^\d+$/;
        return reg.test(value) && value.length === parseInt(MAX_REQUIRED, 10);
    };

    const handleAccountSearch = async (cbuCvuOrAlias) => {
        let cbuCvu;
        let alias;
        if (isValidCbu(cbuCvuOrAlias)) {
            cbuCvu = cbuCvuOrAlias;
        } else {
            alias = cbuCvuOrAlias;
        }

        return new Promise((resolve, reject) => {
            frequentDestinationMidleware
                .createDestinationPreview(cbuCvuOrAlias, "", "", true, null)
                .then(async ({ data: { data }, type }) => {
                    if (type === "W") {
                        if (JSON.parse(data.errorFormat)) {
                            formikProps.setFieldError(data.field, data.NO_FIELD);
                            throw new Error();
                        } else {
                            reject(i18n.get("agenda.create.account.error.exist"));
                        }
                    }
                    const resp = await transferMiddleware.getAccountByCbu(cbuCvu, alias);
                    return resp;
                })
                .then((response) => {
                    const { clientUser, accountDetail, documentType } = response.data.data;
                    if (!clientUser && !accountDetail) {
                        reject(response.data.message);
                    }
                    resolve({
                        dataAccount: {
                            owner: clientUser.firstName,
                            document: clientUser.cuil,
                            documentType,
                            bankName: accountDetail.bankName,
                        },
                    });
                })
                .catch(() => {
                    reject(i18n.get("agenda.create.account.error.notFound"));
                });
        });
    };

    useEffect(() => {
        if (!formikProps.setFieldValue || !prefillAccount || didPrefillSearch.current) {
            return undefined;
        }
        didPrefillSearch.current = true;
        formikProps.setFieldValue("account", prefillAccount);
        if (contact?.name) {
            formikProps.setFieldValue("reference", contact.name);
        }
        if (contact?.email) {
            formikProps.setFieldValue("email", contact.email);
        }

        let cancelled = false;
        handleAccountSearch(prefillAccount)
            .then(() => {
                if (!cancelled) {
                    setRememberValues(true);
                }
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [contact?.email, contact?.name, formikProps, prefillAccount]);

    return (
        <>
            {!uxImprovementsDasboard && <Notification scopeToShow="FrequentDestinationsCreate" />}
            <div className="admin-detail-head px-5">
                {isDesktop && <Head onBack={handleBack} />}
                {(!uxImprovementsDasboard || isDesktop) && (
                    <Head
                        accessibilityTextId="agenda.create.title"
                        centerElement={() => (
                            <Text as="h1" align="center" className="m-0">
                                {i18n.get("agenda.create.title")}
                            </Text>
                        )}
                        titleClassName={
                            !isDesktop ? "header-mobile-title-background-blue white-space-normal align-center" : ""
                        }
                        headerClassName={
                            !isDesktop ? "blue-main-header-mobile blue-main-title-mobile background-transparent" : ""
                        }
                        onBack={!isDesktop && handleBack}
                    />
                )}
            </div>
            <CreateContact
                initialValues={{
                    account: prefillAccount,
                    reference: contact?.name || "",
                    email: contact?.email || "",
                }}
                onSubmit={({ account, reference, email }) => {
                    dispatch(
                        agendaActions.frequentDestinationCreatePreview(
                            {
                                cbu: account,
                                name: reference,
                                email,
                            },
                            formikProps,
                            {},
                        ),
                    );
                }}
                setFormikProps={setFormikProps}
                validationSchema={() =>
                    Yup.lazy((values) => {
                        if (!values.email || !values.reference) {
                            return Yup.object().shape({
                                email: Yup.string().email(i18n.get(`${FORM_ID}.email.invalid`)),
                                reference: Yup.string().required(i18n.get(`${FORM_ID}.name.required`)),
                                account: Yup.string().test(
                                    "is-valid-credit-account",
                                    i18n.get("transfers.validation.invalidFormat"),
                                    (v) => {
                                        if (!v) {
                                            return true;
                                        }
                                        // si tiene 22 digitos numericos es CBU o CVU
                                        if (/^\d{22}$/.test(v)) {
                                            return true;
                                        }
                                        // si tiene entre 6 y 20 caracteres alfanuméricos, guiones o puntos es alias
                                        return /^[a-zA-Z0-9.-]{6,20}$/.test(v);
                                    },
                                ),
                            });
                        }
                        return Yup.object().shape({
                            email: Yup.string().email(i18n.get(`${FORM_ID}.email.invalid`)),
                            reference: Yup.string().max(
                                config.getInteger("frequentDestination.reference.maxLength", 100),
                                i18n.get(`frequentDestination.reference.length.error`),
                            ),
                            account: Yup.string().test(
                                "is-valid-credit-account",
                                i18n.get("transfers.validation.invalidFormat"),
                                (v) => {
                                    if (!v) {
                                        return true;
                                    }
                                    // si tiene 22 digitos numericos es CBU o CVU
                                    if (/^\d{22}$/.test(v)) {
                                        return true;
                                    }
                                    // si tiene entre 6 y 20 caracteres alfanuméricos, guiones o puntos es alias
                                    return /^[a-zA-Z0-9.-]{6,20}$/.test(v);
                                },
                            ),
                        });
                    })
                }
                getFormLabel={(key) => i18n.get(`agenda.create.label.${key}`)}
                loading={fetching}
                searchCallback={handleAccountSearch}
                isValidAccount={rememberValues}
            />
        </>
    );
}

CreateContactPage.propTypes = {
    dispatch: func.isRequired,
    fetching: bool.isRequired,
    isDesktop: bool.isRequired,
    contact: shape({}),
    embeddedInV3Shell: bool,
    lastHrefPathname: string,
};

CreateContactPage.defaultProps = {
    contact: {},
    embeddedInV3Shell: false,
    lastHrefPathname: "",
};

const mapStateToProps = (state) => ({
    fetching: agendaSelectors.getFetching(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    contact: agendaSelectors.getFrequentDestination(state),
    lastHrefPathname: statusSelectors.getPathname(state) || "",
});

export default compose(connect(mapStateToProps), resizableRoute)(CreateContactPage);
